<template>
  <div class="flex-1 flex flex-col min-h-0">
    <div class="flex items-center justify-center mb-5 pt-4">
      <h2 class="text-xl font-semibold text-text-primary capitalize">
        {{ headingPrefix }} · <span
          class="diff-name"
          :style="diffNameStyle"
        >{{ groupName }}</span>
      </h2>
    </div>

    <!-- Loading -->
    <div
      v-if="isLoadingPieces"
      class="flex-1 flex items-center justify-center"
    >
      <div class="w-10 h-10 border-4 border-t-primary border-surface rounded-full animate-spin" />
    </div>

    <!-- Empty (no pieces in this group at all — nothing to filter) -->
    <div
      v-else-if="!pieces?.length"
      class="flex-1 flex flex-col items-center justify-center text-center gap-3"
    >
      <MusicalNoteIcon class="w-12 h-12 text-text-secondary/30" />
      <p class="text-text-secondary">
        {{ emptyMessage }}
      </p>
    </div>

    <!-- Table -->
    <div
      v-else
      class="flex-1 min-h-0 flex flex-col"
    >
      <PieceFilterBar
        v-model:title="titleFilter"
        v-model:language="languageFilter"
        v-model:genre="genreFilter"
        v-model:stars="starsFilter"
        :genre-options="genreOptions"
        :stars-options="starsOptions"
        hide-category
        hide-placement
        :hide-stars="isLessonPiece"
        class="mb-3 px-4"
      />

      <div
        v-if="!filteredPieces.length"
        class="flex-1 flex flex-col items-center justify-center text-center gap-3"
      >
        <MusicalNoteIcon class="w-12 h-12 text-text-secondary/30" />
        <p class="text-text-secondary">
          No pieces match your filters.
        </p>
      </div>

      <template v-else>
        <TableSortControlBar
          :active="sorting.length > 0"
          @reset="resetSort"
        />
        <UTable
          ref="tableScroller"
          v-model:sorting="sorting"
          :data="filteredPieces"
          :columns="pieceColumns"
          class="flex-1 min-h-0"
          sticky
          :ui="{
            th: 'bg-transparent border-b border-white/10 py-2 px-3',
            td: 'py-3 px-3 text-sm border-b border-white/5 cursor-pointer',
            tr: 'hover:bg-white/3 transition-colors',
            base: 'w-full',
          }"
          @select="onPieceSelect"
        >
          <template #grade-cell="{ row }">
            <GradeBadge
              :grade="row.original.highScore ? getGradeForAccuracy(row.original.highScore.accuracy, row.original.highScore.targetLineHidden) : null"
              size="md"
            />
          </template>
          <template #name-cell="{ row }">
            <p class="font-medium text-text-primary">
              {{ row.original.name }}
            </p>
          </template>
          <template #language-cell="{ row }">
            <span
              v-if="row.original.language"
              class="text-sm text-text-secondary capitalize"
            >{{ row.original.language }}</span>
            <span
              v-else
              class="text-sm text-text-secondary/30"
            >—</span>
          </template>
          <template #tempo-cell="{ row }">
            <span
              v-if="row.original.tempo"
              class="text-sm"
            >{{ row.original.tempo }}</span>
            <span
              v-else
              class="text-sm text-text-secondary/30"
            >—</span>
          </template>
          <template #order-cell="{ row }">
            <span class="text-sm text-text-secondary tabular-nums">{{ row.index + 1 }}</span>
          </template>
          <template #stars-cell="{ row }">
            <div
              v-if="(row.original.difficultyStars ?? 0) > 9"
              class="flex items-center gap-1"
            >
              <UIcon
                name="material-symbols-light:kid-star"
                class="text-sm text-gold"
              />
              <span class="text-sm text-gold">×{{ row.original.difficultyStars }}</span>
            </div>
            <div
              v-else
              class="flex items-center gap-0.5"
            >
              <UIcon
                v-for="i in 9"
                :key="i"
                name="material-symbols-light:kid-star"
                :class="i <= (row.original.difficultyStars ?? 0) ? 'text-gold' : 'text-text-secondary/20'"
                class="text-sm"
              />
            </div>
          </template>
        </UTable>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useConvexQuery } from 'convex-vue'
import { api } from '../../convex/_generated/api'
import type { Id } from '../../convex/_generated/dataModel'
import type { TableColumn, TableRow } from '@nuxt/ui'
import { MusicalNoteIcon } from '@heroicons/vue/24/outline'
import { useTableSorting } from '@/composables/useTableSorting'
import { useSaveScrollPosition } from '@/composables/useSaveScrollPosition'
import { usePieceFilters } from '@/composables/usePieceFilters'
import { getGradeForAccuracy } from '@/utils/grade'
import GradeBadge from '@/components/GradeBadge.vue'
import PieceFilterBar from '@/components/PieceFilterBar.vue'
import { difficultyTextColors } from '@/utils/difficultyColor'
import { PIECE_CATEGORY_LABEL_PLURAL } from '@/utils/pieceCategory'
import type { Column } from '@tanstack/table-core'

const props = defineProps<{
  category: 'song' | 'exercise' | 'lesson'
  /** Difficulty id for song/exercise, lesson id for lesson pieces. */
  groupId: Id<'difficulties'> | Id<'lessons'>
  groupName: string
  groupColor: string
}>()

const router = useRouter()

const isLessonPiece = computed(() => props.category === 'lesson')
const headingPrefix = computed(() => PIECE_CATEGORY_LABEL_PLURAL[props.category])
const emptyMessage = computed(() =>
  isLessonPiece.value
    ? 'No pieces in this lesson yet.'
    : `No ${props.category}s found for this difficulty yet.`
)

const diffNameStyle = computed(() => {
  const text = difficultyTextColors(props.groupColor)
  return { '--diff-txt-light': text.light, '--diff-txt-dark': text.dark }
})

const { sorting, getHeader, resetSort } = useTableSorting({
  key: 'songs-piece-list',
  initialSort: [{ id: 'stars', desc: false }],
})

// Picking a different difficulty unmounts this list, which is what makes a fresh
// group start at the top rather than inheriting the previous one's offset.
const tableScroller = useSaveScrollPosition()

const { data: pieces, isPending: isLoadingPieces } = useConvexQuery(
  api.musicalPieces.listByPlacement,
  computed(() => ({
    category: props.category,
    difficultyId: isLessonPiece.value ? undefined : (props.groupId as Id<'difficulties'>),
    lessonId: isLessonPiece.value ? (props.groupId as Id<'lessons'>) : undefined,
  }))
)

// ── Filters ─────────────────────────────────────────────────────────────────
const { titleFilter, languageFilter, genreFilter, starsFilter, filteredPieces, genreOptions, starsOptions } =
  usePieceFilters(pieces)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const pieceColumns = computed<TableColumn<any>[]>(() => [
  {
    id: 'grade',
    accessorFn: (row) => row.highScore?.accuracy ?? -1,
    header: ({ column }) => getHeader(column, 'Grade'),
    size: 48,
    minSize: 44,
    maxSize: 56,
    meta: { class: { td: 'w-12 px-2', th: 'w-12 px-2' } },
  },
  { id: 'name',     accessorKey: 'name',            header: ({ column }) => getHeader(column, 'Title') },
  { id: 'language', accessorKey: 'language',        header: ({ column }) => getHeader(column, 'Language') },
  ...isLessonPiece.value ? [] : [{ id: 'stars', accessorKey: 'difficultyStars', header: ({ column }: { column: Column<unknown> }) => getHeader(column, 'Stars') }],
  { id: 'tempo',    accessorKey: 'tempo',           header: ({ column }) => getHeader(column, 'BPM') },
])

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function onPieceSelect(_e: Event, row: TableRow<any>) {
  const id = row.original?._id as Id<'musicalPieces'>
  if (id) router.push({ name: 'piece-game', params: { pieceId: id } })
}
</script>

<style scoped>
.diff-name {
  color: var(--diff-txt-light);
}
:global(.dark) .diff-name {
  color: var(--diff-txt-dark);
}
</style>
